<template>
  <div class="address-form-input">
    <form class="evollu-form">
      <div class="d-flex justify-content-between align-items-center">
        <h5 v-if="label">{{label}}</h5>
        <slot name="button"></slot>
      </div>

      <div class="row mt-2">
        <div class="col-12">
          <multiselect
            id="country-multiselect"
            :value="selectedCountry"
            :allow-empty="false"
            :multiple="false"
            :options="countries"
            @select="onSelectCountry"
            track-by="code"
            label="name"
            open-direction="bellow"
            select-label=""
            :placeholder="$t('patients.inputs.address.country')"
            deselect-label="×"
            >
            <template slot="option" slot-scope="props">
              <span>{{ props.option.name }}</span>
            </template>
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="value.country" @mousedown.prevent.stop="value.country = null"></div>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <va-input
            id="address-line1"
            v-model="value.line1"
            :placeholder="$t('patients.inputs.address.line1')"
            required
          >
          </va-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <va-input
            id="address-line2"
            v-model="value.line2"
            :placeholder="$t('patients.inputs.address.line2')"
            required
          >
          </va-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <va-input
            id="address-city"
            v-model="value.city"
            :placeholder="$t('patients.inputs.address.city')"
            required
          >
          </va-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <va-input
            id="address-postal-code"
            v-model="value.postal_code"
            :placeholder="$t('patients.inputs.address.code_format')"
            required
          >
          </va-input>
        </div>
      </div>
      <slot name="otherFields" :country="selectedCountry">
      </slot>

    </form>
    <div v-if="showMap" class="mt-3 row">
      <div class="d-flex flex-column flex-md-row align-items-center justify-content-between col-12">

        <div id="select-manually-wrapper">
            <va-popover
              class="d-flex"
              :message="$t('address_convertion.auto_location_tooltip')"
              placement="top">
              <div class="d-flex">
                <slider class="mr-3" v-model="useLocationConversion"/>
                <span class="small-text ">{{$t('address_convertion.auto_location')}}</span>
              </div>
            </va-popover>
          </div>

        <div class="d-flex flex-column">
          <small class="help-text text-danger">
              <b>{{ map.warningText }}</b>
          </small>

          <small v-show="!newPlace" class="help-text text-info">
              <b>{{ map.helpText }}</b>
          </small>
        </div>
      </div>

      <div class="col-12">
        <google-map
          ref="googleMaps"
          class="mt-3"
          :height="'400px'"
          :fitBounds="false"
          :locations="map.locations"
          @map-clicked="onClickMap">
        </google-map>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import GoogleMap from '@/components/maps/google-maps/GoogleMap.vue'
import AddressConverter from '@/utils/AddressConverter'
import Multiselect from 'vue-multiselect'
import Slider from '@/components/common/Slider'
import CountryList from '@/data/CorrectedCountryList'
import { debounce } from 'lodash'

const timeBetweenSearches = 500

export default {
  name: 'address-form-input',
  props: {
    label: String,
    showMap: {
      type: Boolean,
      default: true,
      required: false
    },
    countryFilter: {
      type: Array,
      required: false,
      default: () => {
        return ['PT', 'GB', 'ES']
      }
    },
    newPlace: {
      type: Boolean,
      default: false,
      required: false
    },
    resource: {
      type: Object,
      required: false
    },
    value: {
      default: {
        line1: '',
        line2: '',
        city: '',
        postal_code: '',
        lat: '',
        lng: '',
        country: ''
      },
      required: true
    }
  },
  components: {
    GoogleMap,
    Multiselect,
    Slider
  },
  computed: {
    countries: function () {
      return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
    },
    selectedCountry: function () {
      let selectedCountry = this.value.country ? this.value.country : this.languageCountry
      let match = this.countries.find(element => element.code === selectedCountry)

      if (match) {
        if (!this.value.country) {
          this.$set(this.value, 'country', match.code)
        }
        return match
      }
      return undefined
    },
    languageCountry: function () {
      let locale = Vue.i18n.locale()
      return (locale === 'en' ? 'gb' : locale).toUpperCase()
    },
    watchedProperties: function () {
      let obj = {}
      Object.keys(this.value).filter(key => key !== 'lat' && key !== 'lng' && key !== 'id').forEach(key => {
        obj[key] = this.value[key]
      })
      obj['resourceName'] = this.resource.name
      return obj
    },
    postalCodeLengthForCountry: function () {
      if (this.value.country === 'GB') {
        return 3
      }
      return 4
    }
  },
  data () {
    return {
      map: {
        locations: [],
        addressConverter: new AddressConverter(this.$store.state.app.config.googleMaps.apiKey),
        warningText: undefined,
        helpText: undefined,
      },
      originalLocation: {
        lat: undefined,
        lng: undefined
      },
      hasLoaded: false,
      useLocationConversion: false
    }
  },
  methods: {
    isFilled: function () {
      return this.value.postal_code && this.value.postal_code.length >= this.postalCodeLengthForCountry
    },
    newLocation: function (lat, lng) {
      return { label: this.value.name || '', position: { lat, lng } }
    },
    getAddressLocation: function (forceRequest = false) {
      if (this.useLocationConversion) {
        if (this.isFilled()) {
          this.map.addressConverter
            .addressToCoords(this.watchedProperties, forceRequest)
            .then(result => {
              if (result !== null) { // request canceled
                if (Object.keys(result).length > 0 && result.hasOwnProperty('lat') && result.hasOwnProperty('lng')) { // success
                  console.log('Success', result)
                  this.setLocation(result.lat, result.lng)
                  this.map.warningText = undefined
                } else {
                  console.log('Failed', result)
                  this.removeLocation()
                  this.map.warningText = this.$t('address_convertion.no_results_for_lat_lng')
                }
              }
            })
            .catch(errors => console.log(errors))
        } else {
          this.onNotEnoughAddressData()
        }
      }
    },
    onSelectCountry: function (selection) {
      this.$set(this.value, 'country', selection ? selection.code : null)
    },
    onClickMap: function ({ lat, lng }) {
      if (!this.useLocationConversion) {
        this.setLocation(lat, lng)
      }
    },
    setLocation: function (lat, lng) {
      this.value.lat = lat
      this.value.lng = lng
      this.map.locations = [this.newLocation(lat, lng)]
      this.$refs.googleMaps.updateCenter({ lat, lng })

      if (this.originalLocation.lat !== this.value.lat.toFixed(7) || this.originalLocation.lng !== this.value.lng.toFixed(7)) {
        this.map.helpText = this.$t('address_convertion.unsaved_data')
      }
    },
    removeLocation: function () {
      this.map.locations = []
      this.value.lat = undefined
      this.value.lng = undefined
      this.map.helpText = undefined
      this.map.warningText = undefined
    },
    onNotEnoughAddressData: function () {
      this.map.helpText = undefined
      this.map.warningText = this.$t('address_convertion.no_address_data')
      this.locations = []
    },
    setOriginalLocation: function (lat, lng) {
      let location = this.newLocation(lat, lng)
      this.map.locations.push(location)
      this.originalLocation.lat = lat.toFixed(7)
      this.originalLocation.lng = lng.toFixed(7)
      this.$refs.googleMaps.updateCenter(location.position)
    }
  },
  watch: {
    value: {
      handler: debounce(function (value, oldValue) {
        if (!this.hasLoaded) {
          this.hasLoaded = true

          if (!this.value.country) {
            this.value.country = this.selectedCountry.code
          }

          if (this.value.lat && this.value.lng) {
            this.setOriginalLocation(this.value.lat, this.value.lng)
          } else {
            this.useLocationConversion = true
            this.isFilled() ? this.getAddressLocation() : this.onNotEnoughAddressData()
          }
        }
      }, timeBetweenSearches),
      deep: true
    },
    watchedProperties: {
      handler: debounce(function (value, oldValue) {
        if (this.hasLoaded) this.getAddressLocation()
      }, timeBetweenSearches),
      deep: true
    },
    selectedCountry: {
      handler: function (value) {
        if (!this.value.lat || !this.value.lng) {
          setTimeout(() => {
            this.$refs.googleMaps.updateZoom(value.map_settings.zoom)
            this.$refs.googleMaps.updateCenter(value.map_settings.center)
          }, 250)
        }
      },
      immediate: true
    },
    useLocationConversion: function (newValue) {
      this.removeLocation()
      if (newValue) {
        if (this.isFilled) {
          this.getAddressLocation(true)
        } else {
          this.onNotEnoughAddressData()
        }
        this.$refs.googleMaps.setCursor('grab')
      } else {
        this.map.helpText = this.$t('address_convertion.select_location')
        this.$refs.googleMaps.setCursor('pointer')
      }
    }
  }
}
</script>

<style lang="scss">

.form-group.address {
  margin-bottom: 1rem;
}

</style>

<style scoped lang="scss">
#select-manually-wrapper {
  font-weight: bold;
  font-size: 90%;
}

.help-text b {
  text-decoration: none !important;
}

.control-label {
  z-index: 0 !important;
}

label {
  justify-content: normal;
  font-size: 0.6rem;
  color: #29c4a9;
  font-weight: 600;
  text-transform: uppercase;
  top: -0.6rem;
  left: 0;
  margin-bottom: 0;
}

.custom__tag {
  position: relative;
  display: inline-block;
  padding: 4px 26px 4px 10px;
  border-radius: 5px;
  margin-right: 10px;
  color: #fff;
  line-height: 1;
  background: #29c4a9;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  cursor: pointer;
}

.custom__tag-icon {
  cursor: pointer;
  margin-left: 7px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: normal;
  width: 22px;
  text-align: center;
  line-height: 22px;
  -webkit-transition: all .2s ease;
  transition: all .2s ease;
  border-radius: 5px;
}

.custom__tag-icon:focus, .custom__tag-icon:hover {
  background: #21ab93;
}

.custom__tag-icon:after {
  content: "\D7";
  color: #266d4d;
  font-size: 14px;
}

.custom__tag-icon:focus:after, .custom__tag-icon:hover:after {
  color: #fff;
}

.multiselect__tag-icon:focus, .multiselect__tag-icon:hover {
  background: #21ab93 !important;
}

.multiselect__clear {
  position: absolute;
  right: 33px;
  height: 30px;
  width: 30px;
  display: block;
  cursor: pointer;
  z-index: 1;
}

.multiselect__clear:after, .multiselect__clear:before {
  content: "";
  display: block;
  position: absolute;
  width: 3px;
  height: 12px;
  background: #C0C0C0;
  top: 15px;
  right: 4px;
}

.multiselect__clear:after {
  transform: rotate(-45deg);
}

.multiselect__clear:before {
    transform: rotate(45deg);
}
</style>
